'use client';

import { useState, useEffect, useCallback } from 'react';
import * as Bool from '@/utils/fp/bool';
import { ScreenType } from '@/types/enums/media';
import { useTheme } from '@mui/material';

// Hook
export const useWindowDimensions = () => {
    const theme = useTheme();
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowDimensions, setWindowDimensions] = useState<{
        width: number | undefined;
        height: number | undefined;
        screenType: ScreenType | undefined;
    }>({
        width: undefined,
        height: undefined,
        screenType: undefined,
    });

    const getScreenType = useCallback(
        (windowWidth: number) => {
            const isGreaterOrEqualToDesktopWidth = Bool.isGTE(theme.breakpoints.values.lg);
            const isLessThanOrEqualToTabletWidth = Bool.isLTE(theme.breakpoints.values.md);
            const isMobileWidth = Bool.isLT(theme.breakpoints.values.sm);

            switch (true) {
                case isMobileWidth(windowWidth):
                    return ScreenType.Mobile;
                case isLessThanOrEqualToTabletWidth(windowWidth):
                    return ScreenType.Tablet;
                case isGreaterOrEqualToDesktopWidth(windowWidth):
                    return ScreenType.Desktop;
            }
        },
        [theme.breakpoints.values.lg, theme.breakpoints.values.md, theme.breakpoints.values.sm],
    );

    const updateDimensions = useCallback(() => {
        let frameId: number;

        const update = () => {
            setWindowDimensions((prev) => {
                const width = window.innerWidth;
                const height = window.innerHeight;
                const newScreenType = getScreenType(width);

                if (
                    prev.width === width &&
                    prev.height === height &&
                    prev.screenType === newScreenType
                ) {
                    return prev;
                }

                return { width, height, screenType: newScreenType };
            });
        };

        frameId = requestAnimationFrame(update);
        return () => cancelAnimationFrame(frameId);
    }, [getScreenType]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(document.body);
        updateDimensions();

        return () => {
            resizeObserver.unobserve(document.body);
            resizeObserver.disconnect();
        };
    }, [updateDimensions]);

    return windowDimensions;
};
